import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {fromJS, Map, List} from 'immutable';
import moment from 'moment';
import {capitalize} from 'lodash';
import {useTracking, track} from 'react-tracking';
import {callAction} from 'client/framework';
import {stats} from 'lib/StatsUtil';
import {renderDuration} from 'lib/timeUtils';
import withTracking from 'lib/hocs/withTracking';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import ViewWithSidebar from 'generic/ViewWithSidebar/ViewWithSidebar.react';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import SelectableDateRange from 'generic/SelectableDateRange/SelectableDateRange.react';
import {dateRangeOptions} from 'generic/SelectableDateRange/shared';
import GuideSelector from 'generic/GuideSelector/GuideSelector.react';
import BackgroundTask from 'generic/BackgroundTask/BackgroundTask.react';
import MasteryLevelBreakdown from 'generic/MasteryLevelBreakdown/MasteryLevelBreakdown.react';
import {getModelForResourceType} from 'resources/modelRegistry';
import {getNodeText} from 'lib/reactUtils';

import {
  SelectField,
  SingleSearchAndSelect,
  TabNavigation,
  Card,
  Button,
  Icon,
  Banner,
  LoadingSpinnerLong
} from '@albert-io/atomic';

import EmptyState from '../EmptyState/index.react';

import StudentsPerformance from './PerformanceTables/StudentsPerformance.react';
import QuestionsPerformance from './PerformanceTables/QuestionsPerformance.react';
import StandardsPerformance from './PerformanceTables/StandardsPerformance.react';
import classroomInsightsStore from './ClassroomInsights.store';
import classroomInsightsActions from './ClassroomInsights.actions';

import './classroom-insights.scss';

// Wrap components in tracking
const TrackedAttemptNumberFilter = withTracking(SimpleDropdown, {
  onChange: {
    component: 'InsightsAttemptNumberFilter',
    content: (item) => item.get('value'),
    event: 'click',
    type: 'dropdown'
  }
});
const TrackedSelectField = withTracking(SelectField, {
  onChange: {
    component: 'InsightsReportTypeFilter',
    content: (e) => e.target.value,
    event: 'click',
    type: 'dropdown'
  }
});
const TrackedGuideSelector = withTracking(GuideSelector, {
  onSelect: {
    component: 'InsightsSubjectFilter',
    content: (e, props) => {
      const subject = props.guides.find(
        (currentSubject) => currentSubject.getId() === e.get('subjectId')
      );
      return subject.getName();
    },
    event: 'click',
    type: 'dropdown'
  }
});
const TrackedSelectableDateRange = withTracking(SelectableDateRange, {
  onChange: {
    component: 'InsightsDateFilter',
    content: (option) => option.get('name'),
    event: 'click',
    type: 'dropdown'
  }
});
const TrackedTab = withTracking(TabNavigation.Tab, {
  onClick: {
    content: (e, props) => getNodeText(props.children),
    event: 'click',
    type: 'tab',
    component: 'PerformanceTables'
  }
});

@track({feature: 'Insights'})
export default class ClassroomInsights extends React.Component {
  static propTypes = {
    params: PropTypes.shape({
      classId: PropTypes.string
    }),
    classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1'))
  };

  constructor(props) {
    super(props);

    callAction(classroomInsightsActions.SET_CLASSROOM_ID, props.params.classId);
  }

  render() {
    if (this.props.classroom.getSubjects().isEmpty()) {
      return (
        <EmptyState
          title='No insights data'
          body='Assign practice in order to view all assignment, assessment and independent practice data by standard. Explore our library to discover relevant subjects and content.'
        />
      );
    }
    return (
      <div className='classroom-insights'>
        <ViewWithSidebar
          sidebarItems={[
            {
              label: 'Filters',
              content: (
                <div>
                  <FilterOptions />
                  <ResponseMatrix />
                </div>
              )
            }
          ]}
          mainItems={[
            {
              label: 'Metrics',
              content: <PopulatedPerformanceMetrics />
            },
            {
              label: 'Performance',
              content: <PerformanceTables />
            }
          ]}
        />
      </div>
    );
  }
}

const FilterOptions = awaitMandarkQueries(
  () => ({
    queries: {
      assignments: classroomInsightsStore.getClassroomAssignments('assignment'),
      assessments: classroomInsightsStore.getClassroomAssignments('exam')
    }
  }),

  class FilterOptions extends React.Component {
    static propTypes = {
      assignments: ImmutablePropTypes.list,
      assessments: ImmutablePropTypes.list
    };

    dateRanges = dateRangeOptions.filterNot((range) => {
      return ['All-time', 'Last school year'].includes(range.get('name'));
    });

    reportTypes = [
      {
        value: 'subject',
        label: 'Subject',
        disabled: false
      },
      {
        value: 'assignment',
        label: 'Assignment',
        disabled: this.props.assignments.isEmpty()
      },
      {
        value: 'assessment',
        label: 'Assessment',
        disabled: this.props.assessments.isEmpty()
      }
    ];

    setReportType = (e) => {
      callAction(classroomInsightsActions.SET_REPORT_TYPE, e.target.value);
    };

    handleGuideLevelChange = (guideLevel) => {
      callAction(classroomInsightsActions.SET_GUIDE_LEVEL, {
        subjectId: guideLevel.get('subjectId'),
        guideLevelId: guideLevel.get('guideLevelId')
      });
    };

    handleAttemptChange(item) {
      callAction(classroomInsightsActions.SET_ATTEMPT_NUMBER, item.get('value'));
    }

    makeAttemptDropdownOptions() {
      const mostRecent = Map({
        id: 0,
        name: 'Most recent',
        value: 'mostRecent'
      });

      if (!classroomInsightsStore.areClassroomInsightsPopulated()) {
        return List([mostRecent]);
      }

      const maxAttemptNumbers = classroomInsightsStore
        .getClassroomInsights()
        .getMeta()
        .getMaxAttemptNumber();
      const attemptArray = List(Array(maxAttemptNumbers + 1).keys());
      return attemptArray
        .map((attempt) => {
          return Map({
            id: attempt,
            name: attempt,
            value: attempt
          });
        })
        .shift()
        .unshift(mostRecent);
    }

    render() {
      const guides = classroomInsightsStore.getClassroomSubjectGuides();
      const subject = classroomInsightsStore.getSubject();
      const guideLevel = classroomInsightsStore.getGuideLevelId()
        ? subject.getGuides().first().getGuideLevelById(classroomInsightsStore.getGuideLevelId())
        : null;

      return (
        <Card paddingLevel={2} className='classroom-insights__filter-options'>
          <TrackedSelectField
            className='u-mar-b_2'
            border
            label='Report Type'
            defaultValue={classroomInsightsStore.getReportType()}
            onChange={this.setReportType}
            options={this.reportTypes}
          />
          {classroomInsightsStore.isAssignmentReport() && <AssignmentSearch />}
          {classroomInsightsStore.isAssessmentReport() && <AssessmentSearch />}
          {classroomInsightsStore.isSubjectReport() && (
            <>
              <TrackedGuideSelector
                label='Subject'
                guides={guides}
                onSelect={this.handleGuideLevelChange}
                defaultSubject={subject}
                defaultGuideLevel={guideLevel}
              />
              <TrackedAttemptNumberFilter
                fullWidth
                label='Attempt Number'
                storeName='InsightsAttemptNumberDropdown'
                className='classroom-insights__attempt-dropdown'
                onChange={this.handleAttemptChange}
                options={this.makeAttemptDropdownOptions()}
              />
              <TrackedSelectableDateRange
                storeName={classroomInsightsStore.getSelectableDateRangeStoreName()}
                className='classroom-insights__date-picker'
                options={this.dateRanges}
                defaultDateRange={classroomInsightsStore.getDefaultDateRange()}
                label='Date'
              />
            </>
          )}
        </Card>
      );
    }
  }
);

const AssignmentSearch = () => {
  const {trackEvent} = useTracking();

  if (!classroomInsightsStore.getClassroomAssignments().isResourcePopulated()) {
    return null;
  }

  const makeSearchFilter = (searchString) => {
    return (data) => {
      return data.getName().toLowerCase().indexOf(searchString.toLowerCase()) !== -1;
    };
  };

  const setAssignment = (item) => {
    trackEvent({component: 'InsightsAssignmentFilter', content: item && item.getName()});
    callAction(classroomInsightsActions.SET_ASSIGNMENT, item);
  };

  const assignments = classroomInsightsStore.getClassroomAssignments().getResource();
  const currentAssignment = classroomInsightsStore.getAssignmentOrAssessment();

  return (
    <SingleSearchAndSelect
      data={assignments}
      inputType='text'
      label={capitalize(classroomInsightsStore.getReportType())}
      defaultValue={currentAssignment}
      content={(data) => data.getName()}
      searchFilter={makeSearchFilter}
      onSelect={setAssignment}
      placeholder='Search for an assignment'
    />
  );
};

const AssessmentSearch = () => {
  const {trackEvent} = useTracking();

  if (!classroomInsightsStore.getClassroomAssignments().isResourcePopulated()) {
    return null;
  }

  const makeSearchFilter = (searchString) => {
    return (data) => {
      return data.getName().toLowerCase().indexOf(searchString.toLowerCase()) !== -1;
    };
  };

  const setAssessment = (item) => {
    trackEvent({component: 'InsightsAssessmentFilter', content: item && item.getName()});
    callAction(classroomInsightsActions.SET_ASSESSMENT, item);
  };

  const assessments = classroomInsightsStore.getClassroomAssignments().getResource();
  const currentAssessment = classroomInsightsStore.getAssignmentOrAssessment();

  return (
    <SingleSearchAndSelect
      data={assessments}
      inputType='text'
      label={capitalize(classroomInsightsStore.getReportType())}
      defaultValue={currentAssessment}
      content={(data) => data.getName()}
      searchFilter={makeSearchFilter}
      onSelect={setAssessment}
      placeholder='Search for an assessment'
    />
  );
};

const ResponseMatrix = () => {
  if (
    classroomInsightsStore.isAssessmentReport() ||
    (classroomInsightsStore.isAssignmentReport() &&
      !classroomInsightsStore.getClassroomAssignments().isResourcePopulated())
  ) {
    return null;
  }

  const isSubjectReport = classroomInsightsStore.isSubjectReport();
  const subject = classroomInsightsStore.getSubject();
  const assignmentId = classroomInsightsStore.getAssignmentId();
  const assignmentName = classroomInsightsStore.getAssignmentName();

  const fileName = isSubjectReport
    ? `${classroomStore.getActiveClassroom().getName()} – ${subject.getName()}`
    : `${moment().format('YYYY-MM-DD')}-${classroomStore
        .getActiveClassroom()
        .getName()}-${assignmentName}`;

  const query = isSubjectReport
    ? {
        subject: {
          id: subject.getId()
        }
      }
    : {
        assignment: {
          id: assignmentId
        }
      };

  return (
    <div className='classroom-insights__response-matrix'>
      <BackgroundTask
        task='excel-export'
        args={Map({
          filename: fileName,
          path: '/reports/insight',
          query: {
            teacher: {
              id: classroomStore.getClassroomTeacher().getId()
            },
            classroom: {
              id: classroomStore.getActiveClassroom().getId()
            },
            ...query
          }
        })}
      >
        <Button color='primary'>
          Response matrix (Excel) <Icon className='u-mar-l_1' icon='download' />
        </Button>
      </BackgroundTask>
    </div>
  );
};

const PerformanceMetrics = () => {
  const metrics = classroomInsightsStore.getClassroomInsights().getMeta();
  const correctGuesses = metrics.getClassroomInsightsCountCorrectGuesses();
  const totalGuesses = metrics.getClassroomInsightsCountTotalGuesses();
  const avgAccuracy = stats.percentage(correctGuesses, totalGuesses);

  const timeSpentDuration = moment.duration(metrics.getClassroomInsightsAverageTimeSpent());
  const avgTimeSpent = renderDuration(timeSpentDuration, {style: 'hh:mm:ss'});

  const numOfQuestions = metrics.getClassroomInsightsCountTotalQuestions();
  const masteryBuckets = Map({
    NOT_AVAILABLE: metrics.getClassroomInsightsCountStudentsUnanswered(),
    STRUGGLING: metrics.getClassroomInsightsCountStudentsStruggling(),
    PASSING: metrics.getClassroomInsightsCountStudentsPassing(),
    PROFICIENT: metrics.getClassroomInsightsCountStudentsProficient(),
    EXCELLING: metrics.getClassroomInsightsCountStudentsExcelling(),
    MASTERY: metrics.getClassroomInsightsCountStudentsMastering()
  });

  return (
    <Card className='classroom-insights__performance-metrics'>
      <MasteryLevelBreakdown
        masteryBuckets={masteryBuckets}
        totalCount={metrics.getClassroomInsightsCountStudents()}
        tooltipPosition='bottom'
      />
      <div className='classroom-insights__metrics'>
        <div className='classroom-insights__metric-item'>
          <div className='classroom-insights__metric-label'>Avg Accuracy</div>
          <div className='classroom-insights__metric-content'>{avgAccuracy}%</div>
        </div>
        <div className='classroom-insights__metric-item'>
          <div className='classroom-insights__metric-label'>Avg Time Spent (HH:MM:SS)</div>
          <div className='classroom-insights__metric-content'>{avgTimeSpent}</div>
        </div>
        <div className='classroom-insights__metric-item'>
          <div className='classroom-insights__metric-label'>Unique Questions</div>
          <div className='classroom-insights__metric-content'>{numOfQuestions}</div>
        </div>
      </div>
    </Card>
  );
};

const PopulatedPerformanceMetrics = awaitMandarkQueries(
  () => ({
    queries: [classroomInsightsStore.getClassroomInsightsQuery()],
    loadingElement: (
      <div className='card classroom-insights__performance-metrics u-display_flex u-align-items_center u-justify-content_center'>
        <LoadingSpinnerLong />
      </div>
    )
  }),
  PerformanceMetrics
);

class PerformanceTables extends React.Component {
  constructor() {
    super();

    this.state = {
      activeTab: 'students'
    };
  }

  tabs = fromJS([
    {
      label: 'Students',
      defaultChecked: true,
      onClick: () =>
        this.setState({
          activeTab: 'students'
        })
    },
    {
      label: 'Questions',
      onClick: () =>
        this.setState({
          activeTab: 'questions'
        })
    },
    {
      label: 'Standards',
      onClick: () =>
        this.setState({
          activeTab: 'standards'
        })
    }
  ]);

  setTab = (activeTab) => {
    this.setState({activeTab});
  };

  getTabComponent = (activeTab) =>
    ({
      students: <StudentsPerformance />,
      questions: <QuestionsPerformance />,
      standards: <StandardsPerformance />
    }[activeTab]);

  render() {
    const {activeTab} = this.state;
    const performanceTable = this.getTabComponent(activeTab);
    const isSubjectReport = classroomInsightsStore.isSubjectReport();

    return (
      <>
        {!isSubjectReport && (
          <Banner className='u-mar-b_2'>
            <Banner.Icon iconStyle='regular' icon='exclamation-triangle' />
            Drilldowns not available for this report type.
          </Banner>
        )}
        <Card>
          <TabNavigation align='left'>
            <TrackedTab
              as='button'
              onClick={() => this.setTab('students')}
              isActive={activeTab === 'students'}
            >
              Students
            </TrackedTab>
            <TrackedTab
              as='button'
              onClick={() => this.setTab('questions')}
              isActive={activeTab === 'questions'}
            >
              Questions
            </TrackedTab>
            <TrackedTab
              as='button'
              onClick={() => this.setTab('standards')}
              isActive={activeTab === 'standards'}
            >
              Standards
            </TrackedTab>
          </TabNavigation>
          {performanceTable}
        </Card>
      </>
    );
  }
}
